import { useCallback, useEffect, useState } from "react";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch } from "../../lib/api.js";
import { useMLNodeRun } from "../MLNodeRunContext.js";

// ADR-0106 — Model Evaluate inspector. Binds a Model + version + the true label
// column, then (at run time) scores the labelled upstream set and records a
// model_evaluations row for the champion/challenger comparison. Pure config;
// autosaves via setNodeData.

type ModelListItem = { id: string; name: string; algorithm_family: string };
type VersionRow = { id: string; version_number: number; is_current: boolean };
type ModelDetail = { model: { id: string; name: string; target_column?: string | null }; versions: VersionRow[] };
type PreviewPayload = { columns: Array<{ name: string; type?: string }>; rows: unknown[][]; rowCount: number };

type VersionSel = "current" | number;

const METRIC_LABELS: Record<string, string> = {
  auc: "AUC", accuracy: "Accuracy", precision: "Precision", recall: "Recall", f1: "F1",
  rmse: "RMSE", mae: "MAE", r2: "R²",
  flagged_rate: "Flagged rate", silhouette: "Silhouette", davies_bouldin: "Davies–Bouldin", n_clusters: "Clusters",
};

function cellText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(4);
  return String(v);
}

function readSpec(data: OperationCanvasNodeData): { modelId: string | null; version: VersionSel; labelColumn: string } {
  const sp = (data.config?.spec_model_evaluate as Record<string, unknown> | undefined) ?? {};
  const v = sp.version;
  return {
    modelId: typeof sp.model_id === "string" && sp.model_id ? sp.model_id : null,
    version: typeof v === "number" && Number.isInteger(v) && v > 0 ? v : "current",
    labelColumn: typeof sp.label_column === "string" ? sp.label_column : "",
  };
}

export function OperationInspectorModelEvaluate({
  selectedNode,
  operationId,
  setNodeData,
  flushAndSave,
  pipelineModels = [],
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  flushAndSave: () => Promise<boolean>;
  /** ADR-0116 — models this pipeline's Model Train node(s) have produced, for one-click bind. */
  pipelineModels?: Array<{ id: string; name: string }>;
}) {
  const [models, setModels] = useState<ModelListItem[]>([]);
  const [modelId, setModelId] = useState<string | null>(null);
  const [version, setVersion] = useState<VersionSel>("current");
  const [labelColumn, setLabelColumn] = useState<string>("");
  const [isProvisional, setIsProvisional] = useState(false);
  const [detail, setDetail] = useState<ModelDetail | null>(null);
  const [upstreamColumns, setUpstreamColumns] = useState<string[]>([]);

  // Feature 0010: run state lives in the editor-scoped provider so it survives
  // this inspector unmounting (tab switch / node deselect).
  const { getRun, startRun, stopRun } = useMLNodeRun();
  const run = getRun(selectedNode.id);
  const running = run.running;
  const runError = run.error;
  const preview = run.preview as PreviewPayload | null;
  const metrics = run.metrics;

  const specJson = JSON.stringify(selectedNode.data.config?.spec_model_evaluate ?? null);

  useEffect(() => {
    const s = readSpec(selectedNode.data);
    setModelId(s.modelId); setVersion(s.version); setLabelColumn(s.labelColumn);
    const sp = (selectedNode.data.config?.spec_model_evaluate as Record<string, unknown> | undefined) ?? {};
    setIsProvisional(sp.provisional === true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id, specJson]);

  useEffect(() => {
    void (async () => {
      try { const data = await apiFetch<{ models: ModelListItem[] }>("/api/tenant/models"); setModels(data.models ?? []); }
      catch { setModels([]); }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ columns?: Array<{ name?: string }> }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/upstream-columns`,
        );
        setUpstreamColumns((data.columns ?? []).map((c) => c.name).filter((n): n is string => typeof n === "string" && !!n.trim()));
      } catch { /* surfaced as empty picker */ }
    })();
  }, [operationId, selectedNode.id]);

  useEffect(() => {
    if (!modelId) { setDetail(null); return; }
    void (async () => {
      try { setDetail(await apiFetch<ModelDetail>(`/api/tenant/models/${encodeURIComponent(modelId)}`)); }
      catch { setDetail(null); }
    })();
  }, [modelId]);

  const writeSpec = useCallback((next: { model_id: string | null; version: VersionSel; label_column: string }) => {
    setNodeData(selectedNode.id, (d) => {
      const cfg = (d.config ?? {}) as Record<string, unknown>;
      return { ...d, config: { ...cfg, spec_model_evaluate: next } };
    });
  }, [selectedNode.id, setNodeData]);

  function onModel(id: string) {
    const next = id || null;
    setModelId(next); setVersion("current");
    writeSpec({ model_id: next, version: "current", label_column: labelColumn });
  }
  function onVersion(v: string) {
    const next: VersionSel = v === "current" ? "current" : Number(v);
    setVersion(next); writeSpec({ model_id: modelId, version: next, label_column: labelColumn });
  }
  function onLabel(col: string) { setLabelColumn(col); writeSpec({ model_id: modelId, version, label_column: col }); }

  function onRun() {
    if (running || !modelId) return;
    startRun(selectedNode.id, { operationId, flushAndSave, evaluateModelId: modelId });
  }

  const labelPlaceholder = detail?.model.target_column
    ? `Defaults to the model's target (${detail.model.target_column})`
    : "Defaults to the model's target column";

  const selectedFamily = models.find((m) => m.id === modelId)?.algorithm_family ?? "";
  const isClustering = selectedFamily === "clustering";
  const isAnomaly = selectedFamily === "anomaly";

  return (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div style={{ fontWeight: 600 }}>Model Evaluate</div>

      {/* ADR-0116 — architect-created node waiting to be bound to the pipeline's model. */}
      {isProvisional ? (
        <div className="card" style={{ borderColor: "var(--warn-border, var(--op-node-watch-border))", background: "var(--warn-bg, #fffbeb)", display: "grid", gap: 6 }}>
          {pipelineModels.length > 0 ? (
            <>
              <div><span className="pill pillWarn">Review</span> — bind the model this pipeline trains, then pick the outcome column to measure against</div>
              <div className="actions" style={{ flexWrap: "wrap" }}>
                {pipelineModels.map((m) => (
                  <button key={m.id} className="btn btnPrimary btnSm" type="button" onClick={() => onModel(m.id)}>
                    Bind “{m.name}”
                  </button>
                ))}
              </div>
              <div className="help">For an anomaly model you can leave the label column empty — the evaluation still surfaces the score distribution.</div>
            </>
          ) : (
            <>
              <div><span className="pill pillWarn">Needs setup</span></div>
              <div className="help">Configure and train the model in this pipeline first, then come back to bind it here.</div>
            </>
          )}
        </div>
      ) : null}

      <label className="field">
        <span className="label">Model</span>
        <select className="select" value={modelId ?? ""} onChange={(e) => onModel(e.target.value)}>
          <option value="">— select a model —</option>
          {models.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
        </select>
      </label>

      <label className="field">
        <span className="label">Version</span>
        <select className="select" value={version === "current" ? "current" : String(version)} onChange={(e) => onVersion(e.target.value)} disabled={!detail}>
          <option value="current">Champion (always the promoted version)</option>
          {(detail?.versions ?? []).map((v) => (
            <option key={v.id} value={String(v.version_number)}>v{v.version_number}{v.is_current ? " — champion" : ""}</option>
          ))}
        </select>
      </label>

      {isClustering ? (
        <div className="help">Clustering has no labels — running re-profiles the segment sizes on this data.</div>
      ) : (
        <label className="field">
          <span className="label">{isAnomaly ? "Labeled outcome (optional)" : "Label column (true outcome)"}</span>
          <select className="select" value={labelColumn} onChange={(e) => onLabel(e.target.value)}>
            <option value="">{isAnomaly ? "None — report flagged rate only" : labelPlaceholder}</option>
            {upstreamColumns.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
          {isAnomaly ? <span className="help">Pick a known outcome to score precision/recall against the flagged rows.</span> : null}
        </label>
      )}

      <div className="help">Running records an evaluation visible on the model detail page (and compares versions there).</div>

      <div className="actions">
        {running ? (
          <button className="btn btnDanger" type="button" onClick={() => stopRun(selectedNode.id)}>
            Stop
          </button>
        ) : (
          <button className="btn btnPrimary" type="button" onClick={() => onRun()} disabled={!modelId}>
            Run &amp; preview
          </button>
        )}
      </div>
      {running ? (
        <div className="help">Running{run.startedAt ? ` since ${new Date(run.startedAt).toLocaleString()}` : ""}…</div>
      ) : null}
      {runError ? <div className="help" style={{ color: "var(--danger-text)" }}>{runError}</div> : null}

      {metrics ? (
        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Metrics</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
            {Object.entries(metrics)
              .filter(([k, v]) => typeof v === "number" && k !== "scored_rows")
              .map(([k, v]) => (
                <div key={k} className="card" style={{ padding: 8 }}>
                  <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>{METRIC_LABELS[k] ?? k}</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{(v as number).toFixed(4)}</div>
                </div>
              ))}
          </div>
          {typeof metrics.scored_rows === "number" ? (
            <div className="muted" style={{ fontSize: 12 }}>{(metrics.scored_rows as number).toLocaleString()} rows scored</div>
          ) : null}
        </div>
      ) : null}

      {preview ? (
        <div style={{ display: "grid", gap: 4 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            Predicted vs actual — {preview.rowCount.toLocaleString()} rows · showing first {Math.min(preview.rows.length, 20)}
          </div>
          <div className="tableWrap" style={{ maxHeight: 320, overflow: "auto" }}>
            <table className="table tableCompact">
              <thead><tr>{preview.columns.map((c) => (<th key={c.name}>{c.name}</th>))}</tr></thead>
              <tbody>
                {preview.rows.slice(0, 20).map((r, i) => (
                  <tr key={i}>
                    {preview.columns.map((_, ci) => (<td key={ci}>{cellText((r as unknown[])[ci])}</td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
